import { useQuery } from "@tanstack/react-query";
import { Loader, styled, Tooltip } from "@monolith-forensics/monolith-ui";
import { Case } from "../cases.types";
import { getClients } from "@/api";
import { FolderIcon } from "lucide-react";
import moment from "moment";

const Detail = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailLabel = styled.div`
  font-size: 12px;
  font-weight: bold;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const DetailValue = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.text.primary};
`;

const TooltipContent = ({ caseData }: { caseData?: Case | null }) => {
  const { data, isLoading } = useQuery({
    queryKey: ["clients", "list", { client_id: caseData?.client_id }],
    queryFn: () => getClients({ client_id: caseData?.client_id }),
  });

  if (isLoading) return <Loader />;

  const clientInfo = data?.[0];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <Detail>
        <DetailLabel>Case Open Date</DetailLabel>
        <DetailValue>
          {caseData?.case_open_date
            ? moment
                .utc(caseData?.case_open_date)
                .local()
                .format("MMMM DD, yyyy HH:mm A")
            : "N/A"}
        </DetailValue>
      </Detail>
      <Detail>
        <DetailLabel>Last Activtiy Date</DetailLabel>
        <DetailValue>
          {caseData?.last_activity_date
            ? moment
                .utc(caseData?.last_activity_date)
                .local()
                .format("MMMM DD, yyyy HH:mm A")
            : "N/A"}
        </DetailValue>
      </Detail>
      <Detail>
        <DetailLabel>Case Closed Date</DetailLabel>
        <DetailValue>
          {caseData?.case_closed_date
            ? moment
                .utc(caseData?.case_closed_date)
                .local()
                .format("MMMM DD, yyyy HH:mm A")
            : "N/A"}
        </DetailValue>
      </Detail>
      <Detail>
        <DetailLabel>Case Lead</DetailLabel>
        <DetailValue>
          {caseData?.case_lead?.full_name
            ? caseData?.case_lead?.full_name
            : "N/A"}
        </DetailValue>
      </Detail>
      {clientInfo && (
        <Detail>
          <DetailLabel>Client Info</DetailLabel>
          <DetailValue>
            <div>{clientInfo.name}</div>
            <div>{clientInfo.title}</div>
            <div>{clientInfo.organization}</div>
            <div>{clientInfo.email}</div>
            <div>{clientInfo.address}</div>
            <div>
              {clientInfo.city}, {clientInfo.state} {clientInfo.zipcode}
            </div>
            <div>{clientInfo.office_number}</div>
          </DetailValue>
        </Detail>
      )}
    </div>
  );
};

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;

  font-size: 10px;
  font-weight: bold;
  cursor: pointer;

  padding: 2.5px 5px;
  border-radius: 3px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  color: ${({ theme }) => theme.palette.text.secondary};

  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: ${({ theme }) => theme.palette.primary.main};
  }
`;

export const CaseMetaBadge = ({ caseInfo }: { caseInfo: Case }) => {
  const caseData = caseInfo;

  return (
    <Tooltip
      align="center"
      side="bottom"
      delayDuration={0}
      content={<TooltipContent caseData={caseData} />}
    >
      <StyledContainer>
        <FolderIcon color="orange" size={12} />
        <div>{caseInfo?.case_number}</div>
      </StyledContainer>
    </Tooltip>
  );
};
